import React from 'react';
import { useCookies } from 'react-cookie';

import { fetchAuthSession } from '@aws-amplify/auth';
import {
  useMutation, useQuery,
} from '@tanstack/react-query';
import config from '../../config';

interface RefreshResponse {
  'id_token': string;
  'access_token': string;
  'expires_in': number;
  'token_type': string;
}

async function refreshAccessToken(refreshToken: string): Promise<RefreshResponse> {
  const clientId = config.Cognito.ClientId;
  const url = config.Auth.Url;

  const params = new URLSearchParams();
  params.append('grant_type', 'refresh_token');
  params.append('client_id', clientId);
  params.append('refresh_token', refreshToken);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: params.toString(),
    });

    if (!response.ok) {
      throw new Error('Failed to refresh token');
    }

    const data = await response.json();

    return data;
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw new Error('Failed to refresh token');
  }
}

const cookiePrefix = `CognitoIdentityServiceProvider.${config.Cognito.ClientId}`;

export default function useRefreshTokens(): boolean {
  const userQuery = useQuery(
    ['cognito-use-refresh'],
    () => fetchAuthSession({ forceRefresh: true }).then((res) => res),
  );

  const [lastAuthCookie] = useCookies([`${cookiePrefix}.LastAuthUser`]);
  const lastAuthUser = React.useMemo(() => lastAuthCookie[`${cookiePrefix}.LastAuthUser`], [lastAuthCookie[`${cookiePrefix}.LastAuthUser`]]);

  const userTokenKeyPrefix = React.useMemo(() => {
    if (!lastAuthUser) {
      return undefined;
    }
    return `${cookiePrefix}.${lastAuthUser.replace('@', '%40')}`;
  }, [lastAuthUser]);

  const [tokenCookies, setTokenCookies] = useCookies([
    `${userTokenKeyPrefix}.accessToken`,
    `${userTokenKeyPrefix}.idToken`,
    `${userTokenKeyPrefix}.refreshToken`,
  ]);

  const refreshToken = React.useMemo(() => tokenCookies[`${userTokenKeyPrefix}.refreshToken`], [tokenCookies[`${userTokenKeyPrefix}.refreshToken`]]);
  const accessToken = React.useMemo(() => tokenCookies[`${userTokenKeyPrefix}.accessToken`], [tokenCookies[`${userTokenKeyPrefix}.accessToken`]]);
  const idToken = React.useMemo(() => tokenCookies[`${userTokenKeyPrefix}.idToken`], [tokenCookies[`${userTokenKeyPrefix}.idToken`]]);

  const mutation = useMutation((token: string) => refreshAccessToken(token), {
    onSuccess(data) {
      setTokenCookies(`${userTokenKeyPrefix}.accessToken`, data.access_token, {
        path: '/',
        maxAge: data.expires_in,
        domain: config.Auth.Cookies.CookieDomain,
      });
      window.location.reload();
    },
  });

  React.useEffect(() => {
    if (!accessToken && refreshToken && userQuery.data && !userQuery.data.tokens) {
      mutation.mutate(refreshToken);
    }
  }, [lastAuthUser, accessToken, refreshToken, idToken, userQuery.data]);

  const initializing = React.useMemo(
    () => {
      if (!refreshToken) return false;
      if (userQuery.data?.tokens) return false;
      return true;
    },
    [refreshToken, userQuery.data],
  );

  return initializing;
}
